import type { FC } from "react";
import { useMemo } from "react";

import type { ConsentOptionsListType, GroupedPurposesBySection } from "@ui/components/ConsentSection";
import { mapSectionWithExtendedOptions } from "@ui/components/ConsentSection";
import { Controller, useFormContext } from "@ui/components/Form";
import { OneTrustErrorBanner } from "@ui/components/OneTrustErrorBanner/OneTrustErrorBanner";
import { OptionalConsents } from "@ui/components/OptionalConsents/OptionalConsents";
import { useTranslations } from "@ui/providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import { groupPurposesBySection } from "@ui/utils/purposes";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import type { AcceptAllButtonMode, ConsentsGroupingType, OptionalConsentsLayout, OtPurpose } from "@jti/fe-common";

import styles from "./RegistrationPolicyCheckFields.module.scss";

const { sanitize } = dompurify;

type RegistrationPolicyCheckFieldsProps = {
  fieldName?(name: string): string;
  policyFields?: OtPurpose[];
  disabled?: boolean;
  required?: boolean;
  isError?: boolean;
  oneTrustErrorLinkText?: string;
  oneTrustErrorRedirectUrl?: string;
  oneTrustErrorMessage?: string;
  isOldStyle?: boolean;
  consentOptionsListType?: ConsentOptionsListType;
  consentsGroupingType?: ConsentsGroupingType;
  acceptAllButtonMode?: AcceptAllButtonMode;
  singleOptionConsentsDefaultValue?: "true" | "false";
  isColumnDisplayOfConsents?: boolean;
};

export const RegistrationPolicyCheckFields: FC<RegistrationPolicyCheckFieldsProps> = ({
  fieldName = (name) => name,
  policyFields = [],
  disabled,
  required = true,
  isError,
  oneTrustErrorLinkText,
  oneTrustErrorRedirectUrl,
  oneTrustErrorMessage,
  isOldStyle = true,
  consentOptionsListType,
  consentsGroupingType,
  acceptAllButtonMode,
  singleOptionConsentsDefaultValue,
  isColumnDisplayOfConsents = false,
}) => {
  const { __ } = useTranslations();
  const { setValue, control } = useFormContext();
  const config = useConfig();

  const { mandatoryFields, optionalConsents, optionalConsentsLayout } = useMemo<{
    mandatoryFields: OtPurpose[];
    optionalConsents: OtPurpose[] | GroupedPurposesBySection[];
    optionalConsentsLayout: OptionalConsentsLayout;
  }>(() => {
    const mandatoryFields = policyFields.filter(({ mandatory }) => mandatory);
    const nonMandatoryFields = policyFields.filter(({ mandatory }) => !mandatory);

    if (consentsGroupingType === "single_option") {
      const grouped = groupPurposesBySection(nonMandatoryFields);
      return {
        mandatoryFields,
        optionalConsents: grouped,
        optionalConsentsLayout: "single_option",
      };
    }

    if (consentOptionsListType === "extended") {
      const grouped = groupPurposesBySection(nonMandatoryFields);
      const extended = mapSectionWithExtendedOptions({
        sections: grouped,
        resetDefaultValues: true,
      });
      return { mandatoryFields, optionalConsents: extended, optionalConsentsLayout: "extended" };
    }

    if (consentsGroupingType === "section") {
      const grouped = groupPurposesBySection(nonMandatoryFields);
      return { mandatoryFields, optionalConsents: grouped, optionalConsentsLayout: "section" };
    }

    return { mandatoryFields, optionalConsents: nonMandatoryFields, optionalConsentsLayout: "default" };
  }, [policyFields, consentOptionsListType, consentsGroupingType]);

  const handleSetAllOptionalConsents = (value: string) => {
    if (!optionalConsents.length) return;
    // NOTE: For now, this does not fully support "extended" options like "true--false" or "true--true".
    // It was rejected to implement for now, so we're setting just "true" even for multi-option consents.
    const purposes =
      optionalConsentsLayout === "default"
        ? (optionalConsents as OtPurpose[])
        : (optionalConsents as GroupedPurposesBySection[]).flatMap((section) => section.purposes);
    purposes.forEach(({ id }) => {
      setValue(`purposes.${id}`, value, { shouldValidate: true });
    });
  };

  const handleAcceptAllOptionalConsents = () => {
    handleSetAllOptionalConsents("true");
  };

  const handleDeclineAllOptionalConsents = () => {
    handleSetAllOptionalConsents("false");
  };

  if (isError) {
    return (
      <OneTrustErrorBanner
        title={__("auth.registration.consents.error.title")}
        linkText={oneTrustErrorLinkText}
        redirectUrl={oneTrustErrorRedirectUrl}
        errorMessage={oneTrustErrorMessage || ""}
      />
    );
  }

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.optionalConsentsWrapper]: isOldStyle,
      })}
    >
      <div className={styles.checkboxGroup}>
        {mandatoryFields.map((field) => (
          <Controller
            key={field.id}
            control={control}
            name={fieldName(field.id)}
            render={({ field: hookFormField, fieldState }) => (
              <Checkbox
                {...hookFormField}
                required={field.mandatory}
                checked={Boolean(field.default || hookFormField.value)}
                onCheckedChange={(checked) => hookFormField.onChange(checked)}
                data-testid={`policyCheckbox-${field.id}`}
                id={field.id}
                disabled={disabled}
                status={fieldState.error ? "error" : "default"}
                errorMessage={fieldState.error?.message}
                label={
                  <>
                    {field.mandatory && <span className={styles.required}>*</span>}
                    <span
                      className={styles.description}
                      dangerouslySetInnerHTML={{
                        __html: sanitize(
                          config.ONE_TRUST_DYNAMIC_LINKS
                            ? replaceOneTrustPlaceholdersWithLinks(field.description, config.ONE_TRUST_DYNAMIC_LINKS)
                            : field.description,
                          { ADD_ATTR: ["target"] }
                        ),
                      }}
                    />
                  </>
                }
              />
            )}
            rules={{ required: { value: field.mandatory, message: __("form_validation.field_required") } }}
            defaultValue={field.default}
          />
        ))}
      </div>
      <OptionalConsents
        disabled={disabled}
        required={required}
        purposes={optionalConsents}
        layout={optionalConsentsLayout}
        acceptAllButtonMode={acceptAllButtonMode}
        onAcceptAllClick={handleAcceptAllOptionalConsents}
        onDeclineAllClick={handleDeclineAllOptionalConsents}
        isColumnDisplayOfConsents={isColumnDisplayOfConsents}
        singleOptionConsentsDefaultValue={singleOptionConsentsDefaultValue}
      />
    </div>
  );
};
